import { useIsomorphicLayoutEffect } from "@/hooks/Custom";
import { useAsyncRims, useAsyncSeries, useAsyncWidths } from "@/hooks/Vehicles/Enums";
import { useIsMount } from "@/utils/useIsMount";
import {
  Box,
  Button,
  Flex,
  HStack,
  Skeleton,
  Text,
  useBreakpointValue,
  useToast
} from "@chakra-ui/react";
import { useRouter } from "next/router";
import { useState } from "react";
import { Select2, Select2OptionProps } from "../Select2";
import { Select2Mobile } from "../Select2/indexmobile";

type SearchProps = {
  loading?: boolean;
  loadingAnim?: any;
};

export function SizeSearchDesktop({ loading, loadingAnim }: SearchProps) {
  // ! Env Data
  const DefaultWidth = "185";
  const DefaultSeries = "65";
  const DefaultRim = "15";
  // ! Initial States
  const [Widths, setWidths] = useState<Select2OptionProps[]>([]);
  const [SelectedWidth, setSelectedWidth] = useState<string>(DefaultWidth);
  const [Series, setSeries] = useState<Select2OptionProps[]>([]);
  const [SelectedSeries, setSelectedSeries] = useState<string>(DefaultSeries);
  const [Rims, setRims] = useState<Select2OptionProps[]>([]);
  const [SelectedRim, setSelectedRim] = useState<string>(DefaultRim);

  // ! Controllers & Rullers
  const [SeriesTrigger, setSeriesTrigger] = useState<number>(0);
  const [RimTrigger, setRimTrigger] = useState<number>(0);
  const IsFirstMount = useIsMount();
  const Toast = useToast({
    position: "bottom",
    duration: 3000,
    isClosable: true
  });
  const Router = useRouter();

  const isWideVersion = useBreakpointValue({
    base: false,
    xs: false,
    sm: false,
    md: false,
    lg: true,
    xl: true
  });

  // ! Methods
  async function requestWidths() {
    const [WData, WError] = await useAsyncWidths();

    if (WData && WError === null) {
      setWidths(
        WData?.map((w: string) => {
          return {
            label: w,
            value: w
          };
        })
      );
    }
  }

  async function requestSeries(Width: string) {
    const [SData, SError] = await useAsyncSeries(Width);
    if (SData && SError === null) {
      setSeries(
        SData.map((s: string) => {
          return {
            label: s,
            value: s
          };
        })
      );
    }
  }
  async function requestRims(Width: string, Series: string) {
    const [RData, RError] = await useAsyncRims(Width, Series);
    if (RData && RError === null) {
      setRims(
        RData?.map((r: string) => {
          return {
            label: r,
            value: r
          };
        })
      );
    }
  }

  async function submitSizeSearch(e: string) {
    if (!SelectedWidth) {
      Toast({
        title: "Selecione uma largura para realizar a pesquisa por medida.",
        status: "info",
        containerStyle: {
          color: "white",
          fontFamily: "arial",
          fontWeight: "100"
        }
      });
    } else if (!SelectedRim && !e) {
      Toast({
        title: "Selecione um aro para realizar a pesquisa por medida.",
        status: "info",
        containerStyle: {
          color: "white",
          fontFamily: "arial",
          fontWeight: "100"
        }
      });
    } else {
      loadingAnim(true);
      Router.push({
        pathname: "/pesquisa/medida",
        query: {
          width: `${SelectedWidth}`,
          series: `${SelectedSeries}`,
          rim: `${SelectedRim ?? e ?? ""}`
        }
      });
    }
  }

  // ! Effects
  useIsomorphicLayoutEffect(() => {
    requestWidths();
    requestSeries(DefaultWidth);
    requestRims(DefaultWidth, DefaultSeries);
  }, []);

  // ! Main return
  return (
    <Box>
      <Text textStyle="Regular" color={"ll-gray.500"} mb={"8px"}>
        Busque pela medida:
      </Text>
      <HStack spacing={"13px"}>
        {/* WIDTH SELECT */}
        <Select2
          customW={"105px"}
          width="110px"
          OptionsData={Widths}
          setSelected={setSelectedWidth}
          Selected={SelectedWidth}
          Skew={false}
          Callback={(e: string) => {
            setSelectedSeries(null);
            setSelectedRim(null);
            requestSeries(e);
            setSeriesTrigger((prev) => prev + 1);
          }}
          ScrollAdj={true}
        />
        <Select2
          customW={"105px"}
          MarginR={"-3px"}
          w={"85px"}
          OptionsData={Series}
          setSelected={setSelectedSeries}
          Selected={SelectedSeries}
          Skew={false}
          Trigger={SeriesTrigger}
          Callback={(e: string) => {
            setSelectedRim(null);
            requestRims(SelectedWidth, e);
            setRimTrigger((prev) => prev + 1);
          }}
        />
        {/* RIM SELECT */}
        <Select2
          customW={"105px"}
          MarginR={"-3px"}
          w={"85px"}
          OptionsData={Rims}
          setSelected={setSelectedRim}
          Selected={SelectedRim}
          Trigger={RimTrigger}
          IsLast={true}
          Skew={false}
          Submiter={submitSizeSearch}
        />

        <Button
          width={"100px"}
          variant="primary-button"
          bgColor={"ll-blue.main"}
          _hover={{ opacity: "0.5" }}
          onClick={() => submitSizeSearch(null)}
        >
          <Text>Buscar</Text>
        </Button>
      </HStack>
    </Box>
  );
}
export function SizeSearchMobile({ loading, loadingAnim }: SearchProps) {
  // ! Env Data
  const DefaultWidth = "185";
  const DefaultSeries = "65";
  const DefaultRim = "15";

  const isWideVersion = useBreakpointValue({
    base: false,
    xs: false,
    sm: false,
    md: false,
    lg: true,
    xl: true
  });

  // ! Initial States
  const [Widths, setWidths] = useState<Select2OptionProps[]>([]);
  const [SelectedWidth, setSelectedWidth] = useState<string>(DefaultWidth);
  const [Series, setSeries] = useState<Select2OptionProps[]>([]);
  const [SelectedSeries, setSelectedSeries] = useState<string>(DefaultSeries);
  const [Rims, setRims] = useState<Select2OptionProps[]>([]);
  const [SelectedRim, setSelectedRim] = useState<string>(DefaultRim);

  // ! Controllers & Rullers
  const [SeriesTrigger, setSeriesTrigger] = useState<number>(0);
  const [RimTrigger, setRimTrigger] = useState<number>(0);
  const IsFirstMount = useIsMount();
  const Toast = useToast({
    position: "bottom",
    duration: 3000,
    isClosable: true
  });
  const Router = useRouter();

  // ! Methods
  async function requestWidths() {
    const [WData, WError] = await useAsyncWidths();
    if (WData && WError === null) {
      setWidths(
        WData?.map((w: string) => {
          return {
            label: w,
            value: w
          };
        })
      );
    }
  }
  async function requestSeries(Width: string) {
    const [SData, SError] = await useAsyncSeries(Width);
    if (SData && SError === null) {
      setSeries(
        SData.map((s: string) => {
          return {
            label: s,
            value: s
          };
        })
      );
    }
  }
  async function requestRims(Width: string, Series: string) {
    const [RData, RError] = await useAsyncRims(Width, Series);
    if (RData && RError === null) {
      setRims(
        RData?.map((r: string) => {
          return {
            label: r,
            value: r
          };
        })
      );
    }
  }
  async function submitSizeSearch(e: string = null) {
    if (!SelectedWidth) {
      Toast({
        title: "Selecione uma largura para realizar a pesquisa por medida.",
        status: "info",
        containerStyle: {
          color: "white",
          fontFamily: "arial",
          fontWeight: "100"
        }
      });
    } else if (!SelectedSeries) {
      Toast({
        title: "Selecione uma altura para realizar a pesquisa por medida.",
        status: "info",
        containerStyle: {
          color: "white",
          fontFamily: "arial",
          fontWeight: "100"
        }
      });
    } else if (!SelectedRim && !e) {
      Toast({
        title: "Selecione um aro para realizar a pesquisa por medida.",
        status: "info",
        containerStyle: {
          color: "white",
          fontFamily: "arial",
          fontWeight: "100"
        }
      });
    } else {
      loadingAnim(true);
      Router.push({
        pathname: "/pesquisa/medida",
        query: {
          width: `${SelectedWidth}`,
          series: `${SelectedSeries}`,
          rim: `${SelectedRim ?? e ?? ""}`
        }
      });
    }
  }

  // ! Effects
  useIsomorphicLayoutEffect(() => {
    requestWidths();
    requestSeries(DefaultWidth);
    requestRims(DefaultWidth, DefaultSeries);
  }, []);

  // ! Main return
  return (
    <>
      <Flex
        h={"auto"}
        w={"full"}
        alignItems={"center"}
        justifyContent={"center"}
        direction={"column"}
      >
        <Flex justifyContent={"flex-start"} w={"full"}>
          <Text color="#58585A" textStyle="Regular" mb="10px">
            Busque pela medida
          </Text>
        </Flex>

        {loading ? (
          <Flex w={"full"} gap={"10px"}>
            <Skeleton w="calc(100%/3)" height={"40px"} borderRadius={"50px"} />
            <Skeleton w="calc(100%/3)" height={"40px"} borderRadius={"50px"} />
            <Skeleton w="calc(100%/3)" height={"40px"} borderRadius={"50px"} />
          </Flex>
        ) : (
          <HStack w={"full"}>
            {/* WIDTH SELECT */}
            <Select2Mobile
              typeInput="number"
              MarginR={"0px"}
              customW="calc(100%/3)"
              OptionsData={Widths}
              setSelected={setSelectedWidth}
              Selected={SelectedWidth}
              Skew={false}
              Callback={(e: string) => {
                setSelectedSeries(null);
                setSelectedRim(null);
                requestSeries(e);
                setSeriesTrigger((prev) => prev + 1);
              }}
            />
            {/* SERIES SELECT */}
            <Select2Mobile
              typeInput="number"
              MarginR={"0px"}
              customW="calc(100%/3)"
              OptionsData={Series}
              setSelected={setSelectedSeries}
              Selected={SelectedSeries}
              Trigger={SeriesTrigger}
              Skew={false}
              Callback={(e: string) => {
                setSelectedRim(null);
                requestRims(SelectedWidth, e);
                setRimTrigger((prev) => prev + 1);
              }}
            />
            {/* RIM SELECT */}
            <Select2Mobile
              typeInput="number"
              MarginR={"0px"}
              customW="calc(100%/3)"
              OptionsData={Rims}
              setSelected={setSelectedRim}
              Selected={SelectedRim}
              Trigger={RimTrigger}
              Skew={false}
              IsLast={true}
              Submiter={submitSizeSearch}
            />
          </HStack>
        )}
      </Flex>
      {loading ? (
        <Skeleton mt={"10px"} w="full" height={"40px"} borderRadius={"50px"} />
      ) : (
        <Button
          variant={"primary-button"}
          bgColor={"ll-blue.main"}
          mt="1rem"
          w="full"
          type="submit"
          aria-label="Pesquisar"
          onClick={() => submitSizeSearch()}
        >
          Buscar
        </Button>
      )}
    </>
  );
}
